import React, { useState, useEffect, Suspense, useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { Canvas } from '@react-three/fiber'
import { AdaptiveEvents, Preload } from '@react-three/drei'
import * as THREE from 'three'

import Experience from './Experience.jsx'
import Overlay from './Overlay.jsx'
import CameraController from './utils/CameraController'

import './style.scss'
import VideoPlayer from './components/VideoPlayer.jsx'
import CloseButton from './components/CloseButton.jsx'
import Login from './components/Login.jsx'
import LoadingModal from './components/LoadingModal.jsx';
import MobileWarning from './components/MobileWarning.jsx'
import LoadingScreen from './components/LoadingScreen.jsx'

const root = ReactDOM.createRoot(document.querySelector('#root'))

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('isLoggedIn') === 'true');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [isLoading, setIsLoading] = useState(false);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [data, setData] = useState({});
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [startExperience, setStartExperience] = useState(false);
  const [stationVisible, setStationVisible] = useState(0);
  const [cameraCoords, setCameraCoords] = useState([]);
  const [cameraHeight, setCameraHeight] = useState(0);
  const [modelPosition, setModelPosition] = useState(0);
  const [videoSrc, setVideoSrc] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const [isHomePlaceholderVisible, setIsHomePlaceholderVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetch('./content.json')
        .then(response => response.json())
        .then(json => {
          setData(json);
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && data.stations && data.stations.length > stationVisible) {
      const station = data.stations[stationVisible];
      if (station && station.camera_coords !== undefined) {
        console.log("Updating camera coords and height for station:", stationVisible);
        setCameraCoords(station.camera_coords);
        setCameraHeight(station.camera_height);
      }
    }
  }, [isLoggedIn, data, stationVisible, startExperience]);

  useEffect(() => {
    if (isModelLoaded) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isModelLoaded]);

  const handleLogin = useCallback(() => {
    setIsLoggedIn(true);
    localStorage.setItem('isLoggedIn', 'true');
  }, []);

  const handleCreated = useCallback(({ gl, scene }) => {
    gl.physicallyCorrectLights = true;
    gl.outputEncoding = THREE.sRGBEncoding;
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = 1;
    
    scene.background = new THREE.Color(0xf0f0f0);
    scene.fog = new THREE.Fog(0xf0f0f0, 100, 1000);
    
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;

    gl.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
    gl.debug.checkShaderErrors = false;
  }, []);

  const canvasProps = useMemo(() => ({
    shadows: true,
    dpr: [1, 2],
    gl: {
      antialias: true,
      alpha: false,
      stencil: false,
      depth: true,
      powerPreference: "high-performance",
      failIfMajorPerformanceCaveat: false,
    },
    onCreated: handleCreated
  }), [handleCreated]);

  const resetAppState = useCallback(() => {
    console.log("Resetting app state");
    setShouldAnimate(false);
    setStartExperience(false);
    setStationVisible(0);
    setCameraCoords([]);
    setCameraHeight(0);
    setModelPosition(0);
    setVideoSrc(null);
    setIsTransitioning(false);
    setVideoVisible(false);
    setPopupContent(null);
    setIsHomePlaceholderVisible(true);
  }, []);

  const handleHomeClick = useCallback(() => {
    console.log("Home button clicked");
    resetAppState();
  }, [resetAppState]);

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  if (isMobile) {
    return <MobileWarning />;
  }

  return (
    <>
      {!isModelLoaded && <LoadingScreen />}
      <Overlay 
        setShouldAnimate={setShouldAnimate} 
        shouldAnimate={shouldAnimate}
        setStartExperience={setStartExperience}
        startExperience={startExperience}
        setStationVisible={setStationVisible}
        stationVisible={stationVisible}
        setModelPosition={setModelPosition}
        data={data}
        setCameraCoords={setCameraCoords}
        setCameraHeight={setCameraHeight}
        setIsTransitioning={setIsTransitioning}
        isTransitioning={isTransitioning}
        setVideoSrc={setVideoSrc}
        videoSrc={videoSrc}
        videoVisible={videoVisible}
        isModelLoaded={isModelLoaded}
        popupContent={popupContent} 
        setPopupContent={setPopupContent}
        onHomeClick={handleHomeClick}
        resetAppState={resetAppState}
        isHomePlaceholderVisible={isHomePlaceholderVisible}
        setIsHomePlaceholderVisible={setIsHomePlaceholderVisible}
      />
      <Canvas {...canvasProps}>
        <AdaptiveEvents />
        <Preload all />
        <CameraController 
          shouldAnimate={shouldAnimate}
          startExperience={startExperience}
          cameraCoords={cameraCoords}
          cameraHeight={cameraHeight}
          isTransitioning={isTransitioning}
          setIsTransitioning={setIsTransitioning}
          stationIndex={stationVisible}
          totalStations={data.stations ? data.stations.length : 0}
          setVideoVisible={setVideoVisible}
          videoSrc={videoSrc}
          popupContent={popupContent}
          resetAppState={resetAppState}
        />
        <Suspense fallback={null}>
          <Experience 
            modelPosition={modelPosition}
            stationVisible={stationVisible}
            setVideoSrc={setVideoSrc}
            data={data}
            setIsLoading={setIsLoading}
            setIsModelLoaded={setIsModelLoaded}
          />
        </Suspense>
      </Canvas>
      <LoadingModal isVisible={isLoading} />
      {videoSrc && (
        <div className="video-player-wrapper">
          <div className="stationVideoPlayer">
            <VideoPlayer src={videoSrc}/>
            <CloseButton setVideoSrc={setVideoSrc}/>
          </div>
        </div>
      )}
    </>
  );
}

root.render(<App />);